import { get } from 'lodash'
import { toRef } from 'vue'

import {
  IncidentAttributeId,
  IncidentId,
} from '@collector/sportsapi-client-legacy'
import {
  DetailBaseConfigAttributes,
  DetailConfiguration,
  getAttributeId,
  isDetailsComponentConfiguration,
  SportDetailsConfiguration,
} from '@mobile/views/Relation/DetailsPanel/Sports/composables/useSportOptions'
import { ProbableLinkedIncident } from '@mobile/views/Relation/Shared/RelationDependencies/types'
import {
  AssignableIncidentConfiguration,
  AssignableIncidentConfigurationParticipantType,
  AssignableIncidentsConfiguration,
} from '@mobile/views/Relation/Shared/RelationSportCommonDependencies/AssignableIncidentsConfiguration'

import { IncidentDetailsFillState } from '../enums'

function hasAnyParticipant(
  assignableIncidentConfiguration: AssignableIncidentConfiguration | null,
  probableLinkedIncident: ProbableLinkedIncident,
): boolean {
  const participantRoles: {
    role: AssignableIncidentConfigurationParticipantType
    key: 'subparticipant_id' | 'assistant_id' | 'secondary_assistant_id'
  }[] = [
    { role: 'main', key: 'subparticipant_id' },
    { role: 'secondary', key: 'assistant_id' },
    { role: 'ternary', key: 'secondary_assistant_id' },
  ]

  return participantRoles.some(({ role, key }) =>
    hasParticipant(
      role,
      key,
      assignableIncidentConfiguration,
      probableLinkedIncident,
    ),
  )
}

export function getEmptyIncidents(
  probableLinkedIncidents: ProbableLinkedIncident[],
  secondParticipantIncidents: IncidentId[],
  detailsConfiguration: SportDetailsConfiguration<
    IncidentId,
    IncidentAttributeId
  >,
  assignableIncidentsConfiguration: AssignableIncidentsConfiguration,
): ProbableLinkedIncident[] {
  return probableLinkedIncidents.filter((probableLinkedIncident) => {
    const incident = probableLinkedIncident.incident
    const assignableIncidentConfiguration = get(
      assignableIncidentsConfiguration,
      incident.incident_id,
      null,
    )
    const incidentDetailsConfiguration =
      detailsConfiguration[incident.incident_id]

    if (incidentDetailsConfiguration) {
      if (!isDetailsComponentConfiguration(incidentDetailsConfiguration)) {
        const importantDetailsConditions = getImportantDetailsConditions(
          incidentDetailsConfiguration,
          probableLinkedIncident,
        )
        const detailsGoalkeeper = incidentDetailsConfiguration.find(
          (detail) => detail.name === 'Goalkeeper',
        )

        if (
          detailsGoalkeeper &&
          hasGoalkeeperAndNotEveryDetailsConditions(
            importantDetailsConditions,
            probableLinkedIncident,
            detailsGoalkeeper,
          )
        ) {
          return true
        }

        if (
          !detailsGoalkeeper &&
          hasNotGoalkeeperAndNotEveryDetailsConditions(
            importantDetailsConditions,
          )
        ) {
          return true
        }

        if (importantDetailsConditions.length === 0) {
          return true
        }
      } else if (
        incidentDetailsConfiguration.getIncidentDetailsFillState(incident)
          .state !== IncidentDetailsFillState.Filled
      ) {
        return true
      }
    }

    if (!assignableIncidentConfiguration) {
      return false
    }

    return hasAnyParticipant(
      assignableIncidentConfiguration,
      probableLinkedIncident,
    )
  })
}

function hasGoalkeeperAndNotEveryDetailsConditions(
  importantDetailsConditions: boolean[],
  probableLinkedIncident: ProbableLinkedIncident,
  detailConfiguration: DetailConfiguration<IncidentAttributeId>,
): boolean {
  if (importantDetailsConditions.every((condition: boolean) => condition)) {
    return (
      !detailConfiguration.fulfilledWhen?.(
        [],
        probableLinkedIncident.incident,
      ) || false
    )
  }

  return true
}

function hasNotGoalkeeperAndNotEveryDetailsConditions(
  importantDetailsConditions: boolean[],
): boolean {
  if (!importantDetailsConditions.every((condition) => condition)) {
    return true
  }

  return false
}

function hasParticipant(
  key: 'main' | 'ternary' | 'secondary',
  incidentKey: 'subparticipant_id' | 'assistant_id' | 'secondary_assistant_id',
  assignableIncidentConfiguration: AssignableIncidentConfiguration | null,
  probableLinkedIncident: ProbableLinkedIncident,
): boolean {
  const incident = toRef(probableLinkedIncident, 'incident')

  if (assignableIncidentConfiguration?.participants?.[key]) {
    const isParticipantAvailable =
      assignableIncidentConfiguration.participantsConfiguration?.[
        key
      ]?.availableWhen?.(incident) ?? true
    let isParticipantRequired = false

    if (
      assignableIncidentConfiguration.participantsConfiguration?.[key]
        ?.optional instanceof Function
    ) {
      isParticipantRequired =
        !assignableIncidentConfiguration.participantsConfiguration?.[
          key
        ]?.optional(probableLinkedIncident.incident)
    } else {
      isParticipantRequired =
        !assignableIncidentConfiguration.participantsConfiguration?.[key]
          ?.optional
    }

    return (
      isParticipantAvailable &&
      isParticipantRequired &&
      !incident.value[incidentKey]
    )
  } else {
    return false
  }
}

function getImportantDetailsConditions(
  incidentDetailsConfiguration: DetailConfiguration<IncidentAttributeId>[],
  probableLinkedIncident: ProbableLinkedIncident,
): boolean[] {
  if (!incidentDetailsConfiguration) {
    return []
  }

  let propertyConditions: boolean[] = []

  if (probableLinkedIncident.incident.properties) {
    propertyConditions = incidentDetailsConfiguration.map((detail) => {
      if (detail.important && detail.fulfilledWhen) {
        return detail.fulfilledWhen(
          probableLinkedIncident.incident.attribute_ids,
          probableLinkedIncident.incident,
        )
      } else {
        return true
      }
    })
  }

  const attributeConditions = incidentDetailsConfiguration
    .filter(
      (detail) =>
        detail.important &&
        'attributes' in detail &&
        detail.attributes.length &&
        detail.name !== 'Goalkeeper',
    )
    .map((detail) =>
      (
        detail as DetailBaseConfigAttributes<IncidentAttributeId>
      ).attributes.some((attributeOrAttributeConfig) =>
        probableLinkedIncident.incident.attribute_ids.includes(
          getAttributeId(attributeOrAttributeConfig),
        ),
      ),
    )

  return [...propertyConditions, ...attributeConditions]
}
